import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../app.js";
import { Api } from "../api.js";

import { KeywordListWithKeywords, hasSyncingKeywords } from "../model/keyword_list_with_keywords.js";

import "../components/header-component.js";
import "../components/rating-component.js";
import "../components/keyword-table-component.js";
import "../components/searchbar-component.js";
import "../components/dashboard-drawer-component.js";
import "../components/dashboard-navbar-component.js";
import "./connect-app-modal.js";

import { closeIcon, pencilIcon } from "../utils/icons";
import { FilterEvent, SearchEvent } from "../components/searchbar-component.js";
import { ConnectAppEvent } from "./connect-app-modal.js";
import { Store } from "../utils/store.js";
import { Platform } from "../model/platform.js";
import { Country } from "../model/country.js";
import { CountryFilter, countryFilterToCountry } from "../model/country_filter.js";
import { maskText } from "../utils/utils.js";
import { MAX_KEYWORDS_PER_LIST, MAX_KEYWORDS_PER_ADD } from "../utils/limits.js";

@customElement("list-page")
export class ListPage extends BaseElement {
    @state() listId: string = "";
    @state() list: KeywordListWithKeywords | null = null;
    @state() countryFilter: CountryFilter = CountryFilter.UnitedStates;
    @state() platform: Platform = Platform.iPhone;

    @state() loading = false;
    @state() isEditingName = false;
    @state() blurEnabled = false;

    constructor() {
        super();
        const path = window.location.pathname;
        const match = path.match(/\/list\/([^\/]+)/);
        if (match) {
            this.listId = match[1];
        } else {
            window.location.href = "/search";
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.reloadList();

        const countryByListId = Store.getSettings().countryFilterByListId ?? {};
        this.countryFilter = countryByListId[this.listId] ?? CountryFilter.UnitedStates;

        this.blurEnabled = Store.getSettings().blurSensitiveData ?? false;

        // Listen for blur toggle events
        window.addEventListener("blur-toggle", this.handleBlurToggle);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener("blur-toggle", this.handleBlurToggle);
    }

    private handleBlurToggle = (event: Event) => {
        const customEvent = event as CustomEvent;
        this.blurEnabled = customEvent.detail.enabled;
        this.requestUpdate();
    };

    async reloadList() {
        try {
            this.loading = this.list ? false : true;
            const listWithKeywords = await Api.listGetById(this.listId);
            if (listWithKeywords instanceof Error) {
                console.error("Error fetching list", listWithKeywords.message);
            } else {
                this.list = listWithKeywords;
                if (hasSyncingKeywords(listWithKeywords)) {
                    setTimeout(async () => {
                        this.reloadList();
                    }, 10000);
                }
            }
        } catch (error) {
            console.error("Error fetching list:", error);
        } finally {
            this.loading = false;
        }
    }

    async handleAddKeywords(searchEvent: SearchEvent) {
        try {
            this.loading = true;
            const selectedList = this.list;
            if (!selectedList) {
                alert("No list selected. Please select a list.");
                return;
            }

            const country = countryFilterToCountry(this.countryFilter);
            if (!country) {
                alert("Missing country. Please select a country.");
                return;
            }

            const terms = searchEvent.detail.term
                .split(",")
                .map((term) => term.trim())
                .map((term) => term.toLowerCase());

            if (terms.length > MAX_KEYWORDS_PER_ADD) {
                alert(`Maximum of ${MAX_KEYWORDS_PER_ADD} keywords per request. You entered ${terms.length}.`);
                return;
            }

            const currentCount = this.list?.keywords?.length ?? 0;
            if (currentCount + terms.length > MAX_KEYWORDS_PER_LIST) {
                const remaining = MAX_KEYWORDS_PER_LIST - currentCount;
                alert(`Adding ${terms.length} keywords would exceed the limit of ${MAX_KEYWORDS_PER_LIST} per list. You can add ${remaining} more.`);
                return;
            }

            const result = await Api.addKeywords(terms, country, this.platform, this.list?.id);
            if (result instanceof Error) {
                console.error("Error adding keywords", result.message);
                return;
            }
            await this.reloadList();
        } catch (error) {
            console.error("Error searching:", error);
        } finally {
            this.loading = false;
        }
    }

    async handleFilter(filterEvent: FilterEvent) {
        this.countryFilter = filterEvent.detail.countryFilter;
        this.platform = filterEvent.detail.platform;

        const countryFilterByListId = Store.getSettings().countryFilterByListId ?? {};
        countryFilterByListId[this.listId] = this.countryFilter;

        Store.setSettings({
            ...Store.getSettings(),
            countryFilterByListId: countryFilterByListId,
        });

        await this.reloadList();
    }

    async handleNameUpdate() {
        if (!this.list || !this.list.name.trim()) return;

        try {
            this.loading = true;

            const list = await Api.updateListName(this.list.id, this.list.name.trim());
            if (list instanceof Error) {
                console.error("Error updating list name:", list.message);
                return;
            }
            this.list = list;
        } catch (error) {
            console.error("Error updating list name:", error);
        } finally {
            this.loading = false;
            this.isEditingName = false;
        }
    }

    async connectApp(appId: string, country: Country, platform: Platform) {
        try {
            this.loading = true;

            const result = await Api.updateListApp(this.list!.id, appId, country, platform);
            if (result instanceof Error) {
                console.error("Error connecting list app:", result.message);
                return;
            }
            this.list = result;
        } catch (error) {
            console.error("Error connecting list app:", error);
        } finally {
            this.loading = false;
        }
    }

    async handleDisconnectApp() {
        if (!confirm("Are you sure you want to disconnect this app?")) {
            return;
        }
        try {
            this.loading = true;

            const result = await Api.deleteListApp(this.list!.id);
            if (result instanceof Error) {
                console.error("Error removing list app:", result.message);
                return;
            }
            this.list = result;
        } catch (error) {
            console.error("Error removing list app:", error);
        } finally {
            this.loading = false;
        }
    }

    showConnectAppModal() {
        const modal = document.getElementById("connect-app-modal") as HTMLDialogElement;
        if (modal) {
            modal.showModal();
        }
    }

    handleConnectApp(event: ConnectAppEvent) {
        const appId = event.detail.appId;
        const country = event.detail.country;
        const platform = event.detail.platform;
        this.connectApp(appId, country, platform);
    }

    render() {
        return html`
            <div class="drawer lg:drawer-open">
                <input id="my-drawer" type="checkbox" class="drawer-toggle" />

                <div class="drawer-content">
                    <dashboard-navbar-component></dashboard-navbar-component>

                    ${this.list
                        ? html`
                              <div class="container p-4 md:p-6 lg:p-8 space-y-8">
                                  <div class="flex flex-col gap-4">
                                      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                          ${this.isEditingName
                                              ? html`
                                                    <div class="flex flex-row items-center gap-2">
                                                        <input
                                                            type="text"
                                                            class="input input-bordered w-full max-w-xs"
                                                            .value=${this.list.name}
                                                            @input=${(e: Event) => {
                                                                if (this.list) {
                                                                    this.list.name = (e.target as HTMLInputElement).value;
                                                                }
                                                            }}
                                                            @keydown=${(e: KeyboardEvent) => e.key === "Enter" && this.handleNameUpdate()}
                                                        />
                                                        <button class="btn btn-primary" @click=${() => this.handleNameUpdate()}>Save</button>
                                                        <button class="btn btn-sm btn-circle btn-ghost" @click=${() => (this.isEditingName = false)}>
                                                            <div class="w-6 h-6 ">${closeIcon}</div>
                                                        </button>
                                                    </div>
                                                `
                                              : html`
                                                    <div class="flex flex-row items-center gap-2">
                                                        <div
                                                            class="text-4xl font-semibold ${this.blurEnabled ? "select-none" : ""}"
                                                            style="${this.blurEnabled ? "filter: blur(8px);" : ""}"
                                                        >
                                                            ${this.blurEnabled ? maskText(this.list.name) : this.list.name}
                                                        </div>
                                                        <button class="btn btn-sm btn-circle btn-ghost" @click=${() => (this.isEditingName = true)}>
                                                            <div class="w-6 h-6">${pencilIcon}</div>
                                                        </button>
                                                    </div>
                                                `}
                                          ${this.list?.app === undefined
                                              ? html`
                                                    <button class="btn btn-dash btn-primary" @click=${this.showConnectAppModal}>Connect App</button>
                                                `
                                              : html`
                                                    <div
                                                        class="flex flex-row items-center gap-2 hover:bg-base-200 rounded-lg p-2 cursor-pointer"
                                                        @click=${this.handleDisconnectApp}
                                                    >
                                                        <img
                                                            src=${this.list.app.icon}
                                                            alt=${this.list.app.localization?.title}
                                                            class="w-8 h-8 rounded-lg"
                                                            style="${this.blurEnabled ? "filter: blur(8px) saturate(1.5) brightness(1.1);" : ""}"
                                                        />
                                                        <div class="flex flex-col">
                                                            <div
                                                                class="text-sm font-semibold ${this.blurEnabled ? "select-none" : ""}"
                                                                style="${this.blurEnabled ? "filter: blur(5px);" : ""}"
                                                            >
                                                                ${this.blurEnabled
                                                                    ? maskText(this.list.app.localization?.title || "")
                                                                    : this.list.app.localization?.title}
                                                            </div>
                                                            <div
                                                                class="text-sm text-gray-500 ${this.blurEnabled ? "select-none" : ""}"
                                                                style="${this.blurEnabled ? "filter: blur(5px);" : ""}"
                                                            >
                                                                ${this.blurEnabled
                                                                    ? maskText(this.list.app.localization?.subtitle || "--")
                                                                    : this.list.app.localization?.subtitle ?? "--"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                `}
                                      </div>

                                      <searchbar-component
                                          .countryFilter=${this.countryFilter}
                                          .platform=${this.platform}
                                          ?loading=${this.loading}
                                          ?clearOnSubmit=${true}
                                          ?showAllCountries=${true}
                                          @search=${this.handleAddKeywords}
                                          @filter=${this.handleFilter}
                                      ></searchbar-component>

                                      <keyword-table-component
                                          .list=${this.list}
                                          .countryFilter=${this.countryFilter}
                                          .platform=${this.platform}
                                      ></keyword-table-component>

                                      <div class="text-sm opacity-60 text-center">
                                          ${this.list.keywords?.length ?? 0}/${MAX_KEYWORDS_PER_LIST} Keywords
                                      </div>
                                  </div>
                              </div>
                          `
                        : null}
                </div>

                <div class="drawer-side">
                    <label for="my-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
                    <dashboard-drawer-component .selectedListId=${this.listId}></dashboard-drawer-component>
                </div>

                <connect-app-modal @connect-app=${this.handleConnectApp}></connect-app-modal>
            </div>
        `;
    }
}
